import { Navigate, Route, Routes } from 'react-router-dom';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { AppealsAdmin } from './pages/procurement/AppealsAdmin';
import { AwardDetail } from './pages/procurement/AwardDetail';
import { AwardsRegister } from './pages/procurement/AwardsRegister';
import { ContractDetail } from './pages/procurement/ContractDetail';
import { ContractsRegister } from './pages/procurement/ContractsRegister';
import { Suppliers } from './pages/procurement/Suppliers';
import { TenderDetail } from './pages/procurement/TenderDetail';
import { TenderList } from './pages/procurement/TenderList';
import { CertificateDetail } from './pages/trade/CertificateDetail';
import { Certificates } from './pages/trade/Certificates';
import { InvestmentProjectDetail } from './pages/trade/InvestmentProjectDetail';
import { InvestmentProjects } from './pages/trade/InvestmentProjects';
import { PermitDetail } from './pages/trade/PermitDetail';
import { Permits } from './pages/trade/Permits';
import { TradeReference } from './pages/trade/TradeReference';
import { Enforcement } from './pages/licensing/Enforcement';
import { EnforcementDetail } from './pages/licensing/EnforcementDetail';
import { LicenceApplicationDetail } from './pages/licensing/LicenceApplicationDetail';
import { LicenceApplications } from './pages/licensing/LicenceApplications';
import { LicenceClasses } from './pages/licensing/LicenceClasses';
import { LicenceDetail } from './pages/licensing/LicenceDetail';
import { LicenceRegister } from './pages/licensing/LicenceRegister';
import { MyLicences } from './pages/licensing/MyLicences';
import { Shell, type NavSection } from './ui/Shell';

const SECTIONS: NavSection[] = [
  {
    heading: 'Public Procurement (e-GP)',
    items: [
      { to: '/procurement/tenders', label: 'Tenders', icon: '▤' },
      { to: '/procurement/suppliers', label: 'Suppliers', icon: '⌂' },
      { to: '/procurement/appeals', label: 'Appeals', icon: '⚖' },
      { to: '/procurement/awards', label: 'Awards Register', icon: '★' },
      { to: '/procurement/contracts', label: 'Contracts Register', icon: '☷' },
    ],
  },
  {
    heading: 'Trade & Investment',
    items: [
      { to: '/trade/reference', label: 'Reference Tables', icon: '⌗' },
      { to: '/trade/permits', label: 'Import/Export Permits', icon: '⚓' },
      { to: '/trade/certificates', label: 'Certificates of Origin', icon: '✑' },
      { to: '/trade/investment-projects', label: 'Investment Projects', icon: '◈' },
    ],
  },
  {
    heading: 'Licensing (SLA)',
    items: [
      { to: '/licensing/classes', label: 'Licence Classes', icon: '☷' },
      { to: '/licensing/register', label: 'Licence Register', icon: '⌕' },
      { to: '/licensing/applications', label: 'Applications', icon: '✑' },
      { to: '/licensing/licences', label: 'My / All Licences', icon: '⚑' },
      { to: '/licensing/enforcement', label: 'Enforcement', icon: '⚠' },
    ],
  },
];

/**
 * No whole-app persona gate (unlike every other portal's App.tsx): the
 * Public Procurement tender register, the licence register, and the trade
 * reference tables are genuinely reachable without signing in at all (see
 * server/gateway.ts's /auth/visitor-token). Only specific *actions* — apply,
 * bid, register as a supplier, or any staff screen — check `useAuth().persona`
 * themselves and show an inline sign-in prompt if it's missing, the same way
 * opengov-portal's Parcels.tsx gates one page instead of the whole app.
 */
export function App() {
  return (
    <Routes>
      <Route path="/signin" element={<Login />} />
      <Route path="/signin/complete" element={<SignInComplete />} />

      <Route element={<Shell sections={SECTIONS} />}>
        <Route index element={<Navigate to="/procurement/tenders" replace />} />

        <Route path="procurement" element={<Navigate to="/procurement/tenders" replace />} />
        <Route path="procurement/tenders" element={<TenderList />} />
        <Route path="procurement/tenders/:id" element={<TenderDetail />} />
        <Route path="procurement/suppliers" element={<Suppliers />} />
        <Route path="procurement/appeals" element={<AppealsAdmin />} />
        <Route path="procurement/awards" element={<AwardsRegister />} />
        <Route path="procurement/awards/:id" element={<AwardDetail />} />
        <Route path="procurement/contracts" element={<ContractsRegister />} />
        <Route path="procurement/contracts/:id" element={<ContractDetail />} />

        <Route path="trade" element={<Navigate to="/trade/reference" replace />} />
        <Route path="trade/reference" element={<TradeReference />} />
        <Route path="trade/permits" element={<Permits />} />
        <Route path="trade/permits/:ref" element={<PermitDetail />} />
        <Route path="trade/certificates" element={<Certificates />} />
        <Route path="trade/certificates/:ref" element={<CertificateDetail />} />
        <Route path="trade/investment-projects" element={<InvestmentProjects />} />
        <Route path="trade/investment-projects/:ref" element={<InvestmentProjectDetail />} />

        <Route path="licensing" element={<Navigate to="/licensing/classes" replace />} />
        <Route path="licensing/classes" element={<LicenceClasses />} />
        <Route path="licensing/register" element={<LicenceRegister />} />
        <Route path="licensing/applications" element={<LicenceApplications />} />
        <Route path="licensing/applications/:id" element={<LicenceApplicationDetail />} />
        <Route path="licensing/licences" element={<MyLicences />} />
        <Route path="licensing/licences/:id" element={<LicenceDetail />} />
        <Route path="licensing/enforcement" element={<Enforcement />} />
        <Route path="licensing/enforcement/:id" element={<EnforcementDetail />} />

        <Route path="*" element={<Navigate to="/procurement/tenders" replace />} />
      </Route>
    </Routes>
  );
}
